import { useCallback, useEffect, useState } from 'react';

/**
 * Tope de espera del "Coach está pensando…" sin una letra del coach. Medido
 * sobre 810 respuestas reales (24/08–28/09): la mitad empieza en <1,5 s, 99 de
 * cada 100 en <8,4 s y sólo 1 tardó más de 20 s — la falsa alarma es rarísima.
 */
export const THINKING_WATCHDOG_MS = 20_000;

/**
 * Qué tarjeta mostrar: `opener` = el coach todavía no habló en esta charla (falló
 * el saludo); `reply` = falló la respuesta a un mensaje del user.
 */
export type ReplyFailureKind = 'opener' | 'reply';

export interface ReplyFailureInput {
  /** El server avisó `reply_failed` (`session.replyFailed`). */
  replyFailed: boolean;
  /** "Pensando" crudo: el coach está por responder y todavía no escribió nada. */
  thinking: boolean;
  /** Ya hay alguna burbuja del coach en el hilo (incluidas las de una charla retomada). */
  coachHasSpoken: boolean;
}

export interface ReplyFailureState {
  /** Tarjeta "El coach no pudo responder" a mostrar, o null. */
  failure: ReplyFailureKind | null;
  /**
   * El user tocó Reintentar: la tarjeta del tope se va y el tope se re-arma
   * (si el coach sigue sin escribir otros 20 s, vuelve a aparecer).
   */
  rearm: () => void;
}

/**
 * Fuente única de "el coach no pudo responder" en speak. Dos señales:
 *
 *  1. El AVISO del server (`reply_failed`): el provider del LLM rechazó o se
 *     cortó. Es la causa de fondo — cubre el saludo y la mitad de la charla,
 *     con la voz del coach prendida o apagada.
 *  2. El TOPE de {@link THINKING_WATCHDOG_MS}: red para cualquier otro cuelgue
 *     que deje "pensando" sin aviso (p. ej. la conexión que nunca termina de
 *     abrir). Si la respuesta llega igual, `thinking` cae y la tarjeta se va
 *     sola.
 */
export function useReplyFailure(input: ReplyFailureInput): ReplyFailureState {
  const { replyFailed, thinking, coachHasSpoken } = input;
  const [stuck, setStuck] = useState(false);
  const [armSeq, setArmSeq] = useState(0);
  const watching = thinking && !replyFailed;

  useEffect(() => {
    if (!watching) {
      setStuck(false);
      return;
    }
    const timer = setTimeout(() => setStuck(true), THINKING_WATCHDOG_MS);
    return () => clearTimeout(timer);
  }, [watching, armSeq]);

  const rearm = useCallback((): void => {
    setStuck(false);
    setArmSeq((n) => n + 1);
  }, []);

  const failed = replyFailed || (watching && stuck);
  return { failure: failed ? (coachHasSpoken ? 'reply' : 'opener') : null, rearm };
}
